import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import SEOHead from '@/components/common/SEOHead';
import LanguageSelector, { SupportedLanguage } from '@/components/multilingual/LanguageSelector';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { defaultLanguage, LANGUAGE_COOKIE_NAME, detectBrowserLanguage } from '@/lib/i18n';
import { accessibilityCopy, ACCESSIBILITY_ASSESSED_ON } from '@/lib/i18n/accessibility';
import { SITE_URL } from '@/lib/constants/urls';

const Accessibility = () => {
  const [language, setLanguage] = useState<SupportedLanguage>(defaultLanguage);

  useEffect(() => {
    const saved = localStorage.getItem(LANGUAGE_COOKIE_NAME);
    if (saved && ['en', 'de', 'fr', 'es'].includes(saved)) {
      setLanguage(saved as SupportedLanguage);
    } else {
      const browserLang = detectBrowserLanguage();
      setLanguage(browserLang);
      localStorage.setItem(LANGUAGE_COOKIE_NAME, browserLang);
    }
  }, []);

  const t = accessibilityCopy[language];

  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead
        title="Accessibility statement | PaperVerse"
        description="How accessible PaperVerse is today, the standard we work towards (BFSG, EN 301 549, WCAG 2.1 AA), known limitations, and how to report barriers."
        canonicalUrl={`${SITE_URL}/accessibility`}
      />
      <Navbar />
      <div className="flex-grow pt-24">
        <main className="max-w-3xl mx-auto px-4 pt-8 pb-16">
          <div className="flex flex-wrap items-center justify-between mb-6">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/">Home</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{t.breadcrumb}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            <div className="mt-2 sm:mt-0">
              <LanguageSelector language={language} onChange={setLanguage} />
            </div>
          </div>

          <h1 className="text-3xl font-bold mb-3">{t.title}</h1>
          <p className="text-sm text-muted-foreground mb-8">
            {t.assessedOn}: {ACCESSIBILITY_ASSESSED_ON}
          </p>

          {t.sections.map((section) => (
            <section key={section.title} className="mb-8">
              <h2 className="text-xl font-semibold mb-3">{section.title}</h2>
              {section.body.map((paragraph, i) => (
                <p key={i} className="text-foreground/80 leading-relaxed mb-3">
                  {paragraph}
                </p>
              ))}
            </section>
          ))}
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default Accessibility;
